import { t, Trans } from '@lingui/macro'
import { Currency, Percent } from '@uniswap/sdk-core'
import { useState } from 'react'
import { Map } from 'react-feather'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'

import { RowBetween, RowFixed } from '../Row'
import SettingsTab from '../Settings'
import RefetchButton from './RefetchButton'
import ShareButton from './ShareButton'

const StyledSwapHeader = styled(RowBetween)`
  margin-bottom: 10px;
  font-size: 15px;
  font-weight: 400;
  color: ${({ theme }) => theme.textSecondary};
`

const HeaderButtonContainer = styled(RowFixed)`
  //padding: 0 12px;
  padding: 0 2px;
  gap: 16px;
  user-select: none;
`

const RouteToggleButton = styled.button<{ $enabled: boolean }>`
  border: none;
  margin: 0;
  padding: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $enabled }) => ($enabled ? 'pointer' : 'default')};
  transition: opacity 200ms ease;
  opacity: ${({ $enabled }) => ($enabled ? 1 : 1)};

  &:hover {
    opacity: ${({ $enabled }) => ($enabled ? 0.7 : 1)};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accentAction};
    outline-offset: 2px;
  }
`

const RouteIconContainer = styled(RowFixed)`
  padding: 6px 8px;
  border-radius: 6px;
  align-items: center;
  justify-content: center;
`

const RouteIcon = styled(Map)<{ $active: boolean }>`
  height: 15px;
  width: 15px;
  cursor: pointer;

  > * {
    stroke: ${({ theme, $active }) => ($active ? theme.textPiteas : theme.textSecondary)};
  }
`

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

const TooltipBubble = styled.div<{ $show?: boolean }>`
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 10px;
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 6px;
  white-space: nowrap;
  font-size: 12px;
  color: ${({ theme }) => theme.textPrimary};
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  visibility: ${({ $show }) => ($show ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: none;
  z-index: 1000;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: ${({ theme }) => theme.backgroundOutline};
  }
`

export default function SwapHeader({
  autoSlippage,
  inputCurrency,
  outputCurrency,
  refetchQuote,
  isLoadingTrade,
  hasQuote,
  receiverEnabled,
  onToggleReceiver,
  routePanelOpen = false,
  onToggleRoutePanel,
  routePanelEnabled = true,
  routePending = false,
  hasRoute = false,
}: {
  autoSlippage: Percent
  inputCurrency?: Currency | null
  outputCurrency?: Currency | null
  refetchQuote?: () => void
  isLoadingTrade?: boolean
  hasQuote?: boolean
  receiverEnabled?: boolean
  onToggleReceiver?: () => void
  routePanelOpen?: boolean
  onToggleRoutePanel?: () => void
  routePanelEnabled?: boolean
  routePending?: boolean
  hasRoute?: boolean
}) {
  // const fiatOnRampButtonEnabled = useFiatOnRampButtonEnabled()

  const tooltipText = !routePanelEnabled
    ? t`Show Route`
    : routePending && !hasRoute
    ? t`Fetching now ...`
    : routePanelOpen
    ? t`Hide Route`
    : t`Show Route`

  const handleToggleRoute = () => {
    if (!routePanelEnabled) {
      return
    }
    onToggleRoutePanel?.()
  }

  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <StyledSwapHeader>
      <HeaderButtonContainer>
        <ThemedText.SubHeaderSmall>
          <Trans>Swap</Trans>
        </ThemedText.SubHeaderSmall>
        {/*fiatOnRampButtonEnabled && <SwapBuyFiatButton />*/}
      </HeaderButtonContainer>
      <RowFixed>
        <RefetchButton refetchQuote={refetchQuote} isLoading={isLoadingTrade} hasQuote={hasQuote} />
        <TooltipWrapper
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
        >
          <RouteToggleButton
            type="button"
            onClick={handleToggleRoute}
            aria-pressed={routePanelOpen}
            aria-label={t`Toggle route details`}
            $enabled={routePanelEnabled}
          >
            <RouteIconContainer>
              <RouteIcon $active={routePanelOpen && hasRoute} />
            </RouteIconContainer>
          </RouteToggleButton>
          <TooltipBubble $show={showTooltip}>{tooltipText}</TooltipBubble>
        </TooltipWrapper>
        <ShareButton inputCurrency={inputCurrency} outputCurrency={outputCurrency} />
        <SettingsTab
          autoSlippage={autoSlippage}
          receiverEnabled={receiverEnabled}
          onToggleReceiver={onToggleReceiver}
        />
      </RowFixed>
    </StyledSwapHeader>
  )
}
